/**
 * What the browser remembers about a link (§5.3).
 *
 * « Le navigateur retient la dernière session PAR LIEN - une mémoire locale, par
 * navigateur et par secret, jamais un cookie partagé entre les liens. »
 *
 * A cookie would travel with every request of the origin and would be ONE value
 * for every link a person opened; `localStorage` is per origin too, so the SECRET
 * is in the key. Two links opened from the same browser therefore remember two
 * sessions, and neither can resume the other's.
 *
 * The régime of `lib/chat-outbox.ts`: `localStorage` and not React state,
 * because a state disappears at the `F5` this memory exists to survive; every
 * read and every write in a `try` because a browser in private mode, with site
 * data blocked, or out of quota throws on access rather than answering null -
 * and a person whose browser refuses storage must still be able to open a link.
 *
 * What is stored is the TOKEN and nothing else: no status, no date, no name.
 * The status is asked of the server « avant de dessiner quoi que ce soit »
 * (§5.3), which is what makes a session finished in another tab show as
 * finished here.
 */

const PREFIX = 'matter-extractor:session:';

function keyOf(secret: string): string {
  return `${PREFIX}${secret}`;
}

/** The last session this browser started through this link, or null. */
export function rememberedToken(secret: string): string | null {
  try {
    const value = window.localStorage.getItem(keyOf(secret));
    return value === null || value === '' ? null : value;
  } catch {
    return null;
  }
}

/**
 * Remember the session just started through this link.
 *
 * « Recommencer crée une nouvelle session et n'efface pas l'ancienne »: the
 * OLD one stays in Sessions with its own status and expires on its own. What is
 * replaced is only this browser's shortcut back to one of them.
 */
export function rememberToken(secret: string, token: string): void {
  try {
    window.localStorage.setItem(keyOf(secret), token);
  } catch {
    // A browser that refuses storage costs the person the « Reprendre »
    // shortcut and nothing else: the session itself lives on the server, and
    // its own URL still opens it.
  }
}

/**
 * Forget the shortcut - used when the server answers 404 on a remembered token.
 *
 * That happens on a store reset and on a session moved to the trash (§12.4), and
 * leaving the key would make the page ask about a session that will never come
 * back on every single opening.
 */
export function forgetToken(secret: string): void {
  try {
    window.localStorage.removeItem(keyOf(secret));
  } catch {
    // Nothing to clean up in a browser that has nothing stored.
  }
}
